import React, { memo, useState } from 'react'
import TreatmentMenu from './TreatmentMenu'
import LearnMenu from './LearnMenu'
import AboutMenu from './AboutMenu'
import LocationMenu from './LocationMenu'
import HiddenSSR from '../../atoms/HiddenSSR/HiddenSSR'
import { motion } from 'framer-motion'
import useDidMount from '../../../hooks/useDidMount'
import UnitedStatesMenu from './UnitedStatesMenu'
import {
  MOBILE_NAV_LINKS,
  ABOUT_NAV_LINKS
} from '../../../domain/megaMenu/constants'
import { NavLinks } from './NavLinks'
import {
  Containers,
  INSURANCE_NAV_LINKS
} from '@/domain/useCases/navBar/constants'
import NavSubItems from './NavSubItems'
import { NavBrowseButton } from './NavBrowseButton'
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage'

const homeVariants = {
  hidden: {
    opacity: 0,
    transition: {
      delay: 0.2
    }
  },
  animate: {
    opacity: 1,
    translateX: '0px',
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

const MobileNav = () => {
  const didMount = useDidMount()
  let persistedMenu: string | null = null
  if (didMount) {
    const value = getLocalStorage('menuPersist')
    persistedMenu = typeof value === 'string' ? value : null
  }
  const [state, setState] = useState({ open: persistedMenu })
  const [page, setPage] = useState<string>('home')
  const [container, setContainerState] = useState<Containers | ''>('')

  const setContainer = (container: Containers | string) => {
    setContainerState(container as Containers)
  }

  const onNavItemClick = (item: {
    label: string
    container: Containers
    page: string
  }) => {
    setPage(item.page)
    setContainer(item.container)
  }

  const setOpen = (id: string) => {
    if (state.open === id) {
      setState({ open: null })
    } else {
      setState({
        open: id
      })
      setLocalStorage('menuPersist', id)
    }
  }

  return (
    <nav className="fixed right-0 top-0.5 size-full min-h-screen overflow-y-scroll bg-white">
      <div
        className={`absolute w-full pb-24 transition-transform duration-200 ease-in-out md:hidden`}
      >
        <HiddenSSR
          key="home-links"
          visible={page === 'home'}
        >
          {page === 'home' && didMount && (
            <motion.div
              variants={homeVariants}
              initial="hidden"
              animate="animate"
              className="z-30"
            >
              <ul className="flex w-full flex-col gap-4 px-6 pb-20 pt-6 [&__nav-links:first-of-type]:mt-4">
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  currentlyOpen={state.open as string}
                  items={MOBILE_NAV_LINKS}
                  label="Locations"
                />
                <li className="w-full border-b border-gray-300">
                  <div className="mb-4 flex flex-col gap-y-6">
                    <NavSubItems
                      items={INSURANCE_NAV_LINKS.items}
                      label="Insurance"
                      className="[&_h3]:h-14 [&_h3]:w-full [&_h3]:justify-between [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-gray-1100 [&_svg]:size-6 [&_svg]:font-normal [&_svg]:text-gray-1100"
                      currentlyOpen={state.open as string}
                      toggleOpen={setOpen}
                    />
                    {state.open === 'Insurance' && (
                      <NavBrowseButton
                        className="flex"
                        href="/insurance/"
                        label="Browse all Insurance"
                      />
                    )}
                  </div>
                </li>
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="Find Treatment"
                  currentlyOpen={state.open as string}
                  aria-Label="Browse treatment by menu"
                  items={[
                    {
                      label: 'Conditions',
                      page: 'Conditions',
                      container: Containers.CONDITIONS
                    },
                    {
                      label: 'Treatment Methods',
                      page: 'Conditions',
                      container: Containers.TREATMENT_METHODS
                    },
                    {
                      label: 'Clientele',
                      page: 'Conditions',
                      container: Containers.CLIENTELE
                    },
                    {
                      label: 'Levels of Care',
                      page: 'Conditions',
                      container: Containers.LEVELS_OF_CARE
                    }
                  ]}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  currentlyOpen={state.open as string}
                  aria-Label="Find resources for education on treatment"
                  label="Learn"
                  items={[
                    {
                      label: 'Resource Hub',
                      page: 'Learn',
                      container: Containers.RESOURCE_HUB
                    }
                  ]}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="For Treatment Providers"
                  currentlyOpen={state.open as string}
                  items={[
                    {
                      label: 'Claim Your Profile Today ',
                      page: 'About',
                      container: Containers.FOR_TREATMENT_PROVIDERS
                    },
                    {
                      label: 'Edit Your Profile',
                      page: 'About',
                      container: Containers.EDIT_PROFILE
                    }
                  ]}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="About"
                  aria-Label="Learn more about Recovery.com"
                  currentlyOpen={state.open as string}
                  items={ABOUT_NAV_LINKS}
                />
              </ul>
            </motion.div>
          )}
          {!didMount && (
            <div>
              <ul>
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  currentlyOpen={state.open as string}
                  items={MOBILE_NAV_LINKS}
                  label="Locations"
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  currentlyOpen={state.open as string}
                  items={[
                    {
                      label: 'Insurance',
                      page: 'Conditions',
                      container: Containers.INSURANCE
                    }
                  ]}
                  label="Insurance"
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="Conditions"
                  currentlyOpen={state.open as string}
                  items={[
                    {
                      label: 'Conditions',
                      page: 'Conditions',
                      container: Containers.CONDITIONS
                    },
                    {
                      label: 'Treatment Methods',
                      page: 'Conditions',
                      container: Containers.TREATMENT_METHODS
                    },
                    {
                      label: 'Clientele',
                      page: 'Conditions',
                      container: Containers.CLIENTELE
                    },
                    {
                      label: 'Levels of Care',
                      page: 'Conditions',
                      container: Containers.LEVELS_OF_CARE
                    }
                  ]}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="Learn"
                  currentlyOpen={state.open as string}
                  items={[
                    {
                      label: 'Resource Hub',
                      page: 'Learn',
                      container: Containers.RESOURCE_HUB
                    }
                  ]}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="For Treatment Providers"
                  currentlyOpen={state.open as string}
                  items={{
                    label: 'For Treatment Providers',
                    page: 'About',
                    container: 'For Treatment Providers'
                  }}
                />
                <NavLinks
                  onItemClick={onNavItemClick}
                  onToggleOpen={setOpen}
                  label="About"
                  aria-Label="Learn more about Recovery.com"
                  currentlyOpen={state.open as string}
                  items={[
                    {
                      label: 'About Us',
                      page: 'About',
                      container: Containers.ABOUT_US
                    },
                    {
                      label: 'Our Criteria',
                      page: 'About',
                      container: Containers.OUR_CRITERIA
                    },

                    {
                      label: 'Contact Us',
                      page: 'About',
                      container: Containers.CONTACT_US
                    }
                  ]}
                />
              </ul>
            </div>
          )}
        </HiddenSSR>
        <HiddenSSR
          key="united-states-links"
          visible={page === 'United States'}
        >
          {page === 'United States' && didMount && (
            <UnitedStatesMenu
              setPage={setPage}
              page={page}
            />
          )}
          {!didMount && (
            <UnitedStatesMenu
              setPage={setPage}
              page={page}
            />
          )}
        </HiddenSSR>
        <HiddenSSR
          key="worldwide-links"
          visible={page === 'Worldwide'}
        >
          {page === 'Worldwide' && didMount && (
            <LocationMenu
              setPage={setPage}
              page={page}
            />
          )}
          {!didMount && (
            <LocationMenu
              setPage={setPage}
              page={page}
            />
          )}
        </HiddenSSR>
        {!didMount && (
          <li className="w-full border-b border-gray-300 pb-6">
            <NavSubItems
              items={INSURANCE_NAV_LINKS.items}
              label="Insurance"
              className="[&_svg]:[viewBox:0 0 16 16] [&_h3]:w-full [&_h3]:justify-between [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-gray-1100 [&_svg]:size-6 [&_svg]:font-normal [&_svg]:text-gray-1100 [&_svg]:[text-decoration:none]"
              currentlyOpen={state.open as string}
              toggleOpen={setOpen}
            />

            <NavBrowseButton
              className="flex"
              href="/insurance/"
              label="Browse all Insurance"
            />
          </li>
        )}
        <HiddenSSR
          key="conditions-links"
          visible={page === 'Conditions'}
        >
          {page === 'Conditions' && didMount && (
            <TreatmentMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
          {!didMount && (
            <TreatmentMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
        </HiddenSSR>
        <HiddenSSR
          key="learn-links"
          visible={page === 'Learn'}
        >
          {page === 'Learn' && didMount && (
            <LearnMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
          {!didMount && (
            <LearnMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
        </HiddenSSR>
        <HiddenSSR
          key="about-links"
          visible={page === 'About'}
        >
          {page === 'About' && didMount && (
            <AboutMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
          {!didMount && (
            <AboutMenu
              setPage={setPage}
              page={page}
              container={container}
              setContainer={setContainer}
            />
          )}
        </HiddenSSR>
      </div>
    </nav>
  )
}

export default memo(MobileNav)
